import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import config from '../../../constants/apiConfig';
import { selectUserGuid } from '../../../slices/commonSelectors';
import { useActualIncidentId } from './commonHooks';

export type AuthoredCollection =
  | 'commsResources'
  | 'airOpsSummaries'
  | 'airOpsAircraft'
  | 'airOpsSorties'
  | 'uasSummaries'
  | 'uasMissions'
  | 'performanceRatings'
  | 'rarSummaries'
  | 'rarSites'
  | 'acpSites'
  | 'facilityAssessments'
  | 'tentativeReleases'
  | 'tentativeReleaseItems'
  | 'accountableResourceSheets'
  | 'accountableResourceItems'
  | 'cgCostWorkbooks';

export type AuthoredRow = Record<string, unknown> & {
  id?: string;
  incident_id?: string;
};

const keys = {
  list: (collection: AuthoredCollection, incidentId: string) =>
    ['icsAuthored', collection, incidentId] as const,
};

function collectionUrl(collection: AuthoredCollection, incidentId: string) {
  return `${config.apiGateway.ics_authored}/${collection}/incident/${incidentId}`;
}

export function useAuthoredRows(
  collection: AuthoredCollection,
  periodNumber?: number
) {
  const incidentId = useActualIncidentId();
  return useQuery({
    queryKey: [...keys.list(collection, incidentId), periodNumber] as const,
    queryFn: async () => {
      const resp = await axios.get<AuthoredRow[]>(
        collectionUrl(collection, incidentId),
        { params: periodNumber != null ? { periodNumber } : undefined }
      );
      return resp.data;
    },
    enabled: Boolean(incidentId),
  });
}

export function useCreateAuthoredRow(collection: AuthoredCollection) {
  const incidentId = useActualIncidentId();
  const userGuid = useSelector(selectUserGuid);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: Record<string, unknown>) => {
      const resp = await axios.post<AuthoredRow>(
        collectionUrl(collection, incidentId),
        { ...data, user_guid: userGuid }
      );
      return resp.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: keys.list(collection, incidentId),
      });
    },
    onError: (error: { message?: string }) => {
      toast.error(error.message ?? 'Could not save the form row.');
    },
  });
}

export function useUpdateAuthoredRow(collection: AuthoredCollection) {
  const incidentId = useActualIncidentId();
  const userGuid = useSelector(selectUserGuid);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      entityId,
      ...data
    }: Record<string, unknown> & { entityId: string }) => {
      return await axios.put(
        `${collectionUrl(collection, incidentId)}/${entityId}`,
        { ...data, user_guid: userGuid }
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: keys.list(collection, incidentId),
      });
    },
    onError: (error: { message?: string }) => {
      toast.error(error.message ?? 'Could not update the form row.');
    },
  });
}

/**
 * Remove a row.
 *
 * Authored rows are versioned and append-only, so the update route treats an
 * archived flag as the delete: the row stops coming back from the list query
 * and stops printing, while its history survives.
 */
export function useDeleteAuthoredRow(collection: AuthoredCollection) {
  const incidentId = useActualIncidentId();
  const userGuid = useSelector(selectUserGuid);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (entityId: string) => {
      return await axios.put(
        `${collectionUrl(collection, incidentId)}/${entityId}`,
        { archived: true, user_guid: userGuid }
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: keys.list(collection, incidentId),
      });
    },
    onError: (error: { message?: string }) => {
      toast.error(error.message ?? 'Could not remove the form row.');
    },
  });
}
